import { Capacitor } from '@capacitor/core';
import L from 'leaflet';
import { useEffect } from 'react';
import {
  Circle,
  MapContainer,
  Marker,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
  ZoomControl,
} from 'react-leaflet';

import { Modal } from '@/components';
import styles from '@pages/DockagePage/components/DockagesMap.module.scss';
import type {
  MapCoords,
  PointDto,
  RadiusSearchParams,
} from '@pages/DockagePage/components/DockagesMap.tsx';

type MapModalProps = {
  open: boolean;
  onClose: () => void;
  onMapClick: () => void;
  center: [number, number];
  onSearch?: (params: RadiusSearchParams) => void;
  radiusMiles: number;
  searchCenter?: MapCoords | null;
  validPoints?: PointDto[];
  markerIcon: L.Icon;
  radiusMeters: number;
  setActivePointId: (id: string | null) => void;
  activePointId: string | null;
};

const MapModal = ({
  open,
  onClose,
  onMapClick,
  center,
  onSearch,
  radiusMiles,
  searchCenter,
  validPoints,
  markerIcon,
  radiusMeters,
  setActivePointId,
  activePointId,
}: MapModalProps) => {
  const handlePick = (lat: number, lng: number) => {
    onSearch!({ lat, lng, radiusMiles });
  };
  return (
    <Modal open={open} onClose={onClose}>
      <MapContainer
        center={center}
        zoom={12}
        zoomControl={false}
        className={styles.modalMapContainer}
      >
        <>
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ZoomControl position="bottomright" />

          <MapSizeInvalidator />
          <MapCenterUpdater center={center} />
          <ClickHandler onPick={handlePick} onMapClick={onMapClick} />
          <OpenInMapsButton />

          {searchCenter && (
            <>
              <Marker position={[searchCenter.lat, searchCenter.lng]} icon={markerIcon} />
              <Circle center={[searchCenter.lat, searchCenter.lng]} radius={radiusMeters} />
            </>
          )}

          {validPoints?.map((p) => (
            <Marker
              key={p?.id}
              position={[p?.lat, p?.lng]}
              icon={markerIcon}
              eventHandlers={{
                click: (event) => {
                  L.DomEvent.stopPropagation(event.originalEvent);
                  setActivePointId(p.id);
                  handlePick(p.lat, p.lng);
                },
              }}
            >
              <Tooltip
                direction="top"
                offset={[0, -32]}
                opacity={1}
                permanent={activePointId === p?.id}
              >
                {p?.address || p?.name || `${p?.lat?.toFixed(4)}, ${p?.lng?.toFixed(4)}`}
              </Tooltip>
            </Marker>
          ))}
        </>
      </MapContainer>
    </Modal>
  );
};

export default MapModal;

const buildMapsAppUrl = (lat: number, lng: number) => {
  const coords = `${lat},${lng}`;

  switch (Capacitor.getPlatform()) {
    case 'ios':
      return `maps://?ll=${coords}&q=${encodeURIComponent('Dropped pin')}`;
    case 'android':
      return `geo:${coords}?q=${coords}`;
    default:
      return `https://www.google.com/maps/search/?api=1&query=${coords}`;
  }
};

export function OpenInMapsButton() {
  const map = useMap();

  const openInMapsApp = () => {
    const { lat, lng } = map.getCenter();
    const url = buildMapsAppUrl(lat, lng);

    if (Capacitor.isNativePlatform()) {
      window.location.href = url;
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      type="button"
      style={{ zIndex: 1000, position: 'absolute', bottom: 0, padding: '8px', cursor: 'pointer' }}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        openInMapsApp();
      }}
    >
      Open in Maps
    </button>
  );
}

export const MapSizeInvalidator = () => {
  const map = useMap();

  useEffect(() => {
    const invalidate = () => map.invalidateSize();
    const frame = requestAnimationFrame(invalidate);
    window.addEventListener('resize', invalidate);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', invalidate);
    };
  }, [map]);

  return null;
};

function MapCenterUpdater({ center }: { center: [number, number] }) {
  const map = useMap();
  const centerKey = `${center[0]},${center[1]}`;

  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, centerKey, map]);

  return null;
}

function ClickHandler({
  onPick,
  onMapClick,
}: {
  onPick: (lat: number, lng: number) => void;
  onMapClick?: () => void;
}) {
  useMapEvents({
    click(e) {
      onMapClick?.();
      onPick(e?.latlng?.lat, e?.latlng?.lng);
    },
  });
  return null;
}
